import EditOutlinedIcon from '@material-ui/icons/EditOutlined'
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined'
import { OffsetOption } from '@shared/core/custom-offsets'
import { LANGUAGE_DISPLAY_NAMES } from '@shared/core/localized-defaults'
import { MessageTemplateDto } from '@shared/core/message-templates'
import { type ReactNode, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useCalendars } from 'src/modules/calendar/hooks/use-calendars'
import { Button, Icon, Loader } from 'src/modules/core/components'
import { useConfirmationOffsets } from 'src/modules/custom-offsets/hooks/use-confirmation-offsets'
import { useFollowUpOffsets } from 'src/modules/custom-offsets/hooks/use-follow-up-offsets'
import { useReminderOffsets } from 'src/modules/custom-offsets/hooks/use-reminder-offsets'
import { useMessageTemplates } from 'src/modules/message-templates/hooks/use-message-templates'
import { useMessageLanguage } from 'src/modules/user/hooks/use-message-language'
import { useProfile } from 'src/modules/user/hooks/use-profile'
import classes from './settings-templates-tab.module.scss'
import { TemplateFormDrawer } from './template-form-drawer'

export function SettingsTemplatesTab() {
  const profile = useProfile()
  const { formatMessage } = useIntl()
  const { language: accountLanguage } = useMessageLanguage()
  const { data: templates, isLoading: isLoadingTemplates } = useMessageTemplates()
  const { data: calendars } = useCalendars()
  const isLoading = useMemo(() => profile.isLoading || isLoadingTemplates, [profile.isLoading, isLoadingTemplates])
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplateDto | undefined>()

  // Custom offsets
  const confirmationOffsets = useConfirmationOffsets()
  const reminderOffsets = useReminderOffsets()
  const followUpOffsets = useFollowUpOffsets()

  const handleOpenDrawer = (template?: MessageTemplateDto) => {
    setSelectedTemplate(template)
    setIsDrawerOpen(true)
  }

  const handleCloseDrawer = () => {
    setSelectedTemplate(undefined)
    setIsDrawerOpen(false)
  }

  const getTitleForMessageType = (offsets: number[], options: OffsetOption[]) => {
    return offsets
      .map((offset, index) => {
        const option = options.find((option) => option.value === offset.toString())
        return index === offsets.length - 1 ? option?.label : option?.short
      })
      .join(', ')
  }

  const getTemplateLanguageLabel = (template: MessageTemplateDto) =>
    template.language ? (
      LANGUAGE_DISPLAY_NAMES[template.language].native
    ) : (
      <span className={classes.accountLanguage}>
        {formatMessage(
          { id: 'settings.messageLanguage.template.listAccountLanguage' },
          {
            language: LANGUAGE_DISPLAY_NAMES[accountLanguage].english,
            value: (chunks: ReactNode[]) => <span className={classes.accountLanguageValue}>{chunks}</span>,
          },
        )}
      </span>
    )

  if (isLoading) return <Loader isLoading={isLoading} />

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        <span>Manage your SMS reminder templates. Customize the timing, content and language to match your needs.</span>
        <Button variant="primary" className={classes.newTemplateButton} onClick={() => handleOpenDrawer()}>
          New Template
        </Button>
      </div>

      <div className={classes.tableWrapper}>
        <table>
          <thead>
            <tr>
              <th className={classes.templateName}>Template Name</th>
              <th className={classes.calendars}>Calendars</th>
              <th className={classes.businessName}>Business Name</th>
              <th className={classes.language}>Language</th>
              <th className={classes.messagePreview}>Messages</th>
              <th className={classes.actions}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {templates?.map((template) => (
              <tr key={template.id} onClick={() => handleOpenDrawer(template)} className={classes.templateRow}>
                <td className={classes.templateName}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {template.template_name}
                    {template.is_default && <span className={classes.defaultTemplate}>Default</span>}
                  </div>
                </td>
                <td className={classes.calendars}>
                  {template.default_for_calendars
                    ?.map((calendarId) => {
                      const calendar = calendars?.find((cal) => cal.id === calendarId)
                      return calendar?.title || 'Untitled Calendar'
                    })
                    .filter(Boolean)
                    .sort((a, b) => a.localeCompare(b))
                    .join(', ')}
                </td>
                <td className={classes.businessName}>{template.sender_name}</td>
                <td className={classes.language}>{getTemplateLanguageLabel(template)}</td>
                <td className={classes.messageTypes}>
                  <ul>
                    {template.confirmation_enabled && (
                      <li title={getTitleForMessageType(template.confirmation_offsets, confirmationOffsets)}>
                        <Icon name="messageTypeConfirmation" className={classes.messageTypeIcon} />
                        <span>Confirmation</span>
                      </li>
                    )}
                    {template.reminder_enabled && (
                      <li title={getTitleForMessageType(template.reminder_offsets, reminderOffsets)}>
                        <Icon name="messageTypeReminder" className={classes.messageTypeIcon} />
                        <span>Reminder</span>
                      </li>
                    )}
                    {template.follow_up_enabled && (
                      <li title={getTitleForMessageType(template.follow_up_offsets, followUpOffsets)}>
                        <Icon name="messageTypeFollowUp" className={classes.messageTypeIcon} />
                        <span>Follow Up</span>
                      </li>
                    )}
                  </ul>
                </td>
                <td className={classes.actions}>
                  <button title="View">
                    <VisibilityOutlinedIcon />
                  </button>
                  <button title="Edit">
                    <EditOutlinedIcon />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <TemplateFormDrawer isOpen={isDrawerOpen} onClose={handleCloseDrawer} template={selectedTemplate} />
    </div>
  )
}
